import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const INITIAL_VISIBLE_RESULTS = 100;
const RESULTS_STEP = 100;

const Icon = ({ name, size = 20 }) => {
  const icons = {
    chat: (
      <>
        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
        <path d="M8 9h8M8 13h5" />
      </>
    ),
    close: <path d="M18 6 6 18M6 6l12 12" />,
    trash: (
      <>
        <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
        <path d="M10 11v5M14 11v5" />
      </>
    ),
    sparkle: (
      <>
        <path d="m12 3 1.35 3.65L17 8l-3.65 1.35L12 13l-1.35-3.65L7 8l3.65-1.35L12 3Z" />
        <path d="m19 14 .8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14Z" />
        <path d="m5 15 .65 1.35L7 17l-1.35.65L5 19l-.65-1.35L3 17l1.35-.65L5 15Z" />
      </>
    ),
    chevron: <path d="m6 9 6 6 6-6" />,
    send: (
      <>
        <path d="m22 2-7 20-4-9-9-4Z" />
        <path d="M22 2 11 13" />
      </>
    ),
    bot: (
      <>
        <rect x="4" y="6" width="16" height="13" rx="3" />
        <path d="M12 2v4M8 11h.01M16 11h.01M9 15h6" />
      </>
    ),
    user: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icons[name]}
    </svg>
  );
};

export default function Chatbot({
  suggestions: propSuggestions = [],
  title = "Pickkup Support",
}) {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const suggestionsRef = useRef(null);

  const API_URL = "https://apipythonchat.pickkup.io";

  const defaultSuggestions = useMemo(
    () =>
      propSuggestions.length > 0
        ? propSuggestions.map((q) => ({
            label: q,
            question: q,
          }))
        : [
            {
              label: "Today's Trips",
              question: "Today's Trips",
            },
            {
              label: "Present Users",
              question: "Present User",
            },
            {
              label: "Absent Users",
              question: "Absent User",
            },
            {
              label: "Service Center",
              question: "Current month's Service center vehicles",
            },
            {
              label: "Top 5 Vehicles",
              question: "Show Top 5 vehicles by trips in current month",
            },
            {
              label: "Top Revenue Vehicle",
              question: "Current month's Vehicle generated most revenue",
            },
            {
              label: "Monthly Revenue",
              question: "Current month revenue",
            },
            {
              label: "Client Revenue",
              question: "Client wise revenue current month",
            },
            {
              label: "Revenue / Vehicle",
              question: "Current month's Revenue per vehicle",
            },
            {
              label: "Compare Revenue",
              question: "Compare revenue with last month",
            },
            {
              label: "Revenue Trend",
              question: "Revenue trend for last 6 months",
            },
            {
              label: "Vehicle Productivity",
              question: "Show vehicle productivity analysis for current month",
            },
            {
              label: "Today's Attendance",
              question: "Vehicle who has marked attendance today",
            },
            {
              label: "No Attendance",
              question: "Moving vehicles without attendance today",
            },
            {
              label: "Top User Expense",
              question: "User spent most in current month",
            },
            {
              label: "Expense Category",
              question: "Show highest expense category for current month",
            },
            {
              label: "Expense Trend",
              question: "Expense trend for last 6 months",
            },
            {
              label: "Compare Expenses",
              question: "Compare expenses with previous month",
            },
            {
              label: "Expense Analysis",
              question: "Show expense analysis in current month",
            },
            {
              label: "Expense Vehicle Analysis",
              question: "show vehicle wise expense current month",
            },
            // {
            //   label: "Vehicles > 150 KM",
            //   question: "Show vehicles above 150 km today",
            // },
          ],
    [propSuggestions],
  );

  const getTime = () =>
    new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  useEffect(() => {
    if (chatOpen && chatMessages.length === 0) {
      setChatMessages([
        {
          sender: "bot",
          text: "Hi! I’m your Pickkup assistant. Ask me about trips, revenue, expenses, attendance, vehicles, or service-center activity.",
          time: getTime(),
        },
      ]);
    }
  }, [chatOpen, chatMessages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, loading]);

  useEffect(() => {
    if (chatOpen) {
      window.setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [chatOpen]);

  const formatResponse = (data, visibleLimit = INITIAL_VISIBLE_RESULTS) => {
    if (!data) return "No response received.";

    if (data.summary) return data.summary;

    if (Array.isArray(data.data)) {
      const total = data.data.length;
      const limit = Math.min(visibleLimit, total);

      const list = data.data
        .slice(0, limit)
        .map((item, i) => {
          const name =
            item.numberplate ||
            item.vehicle ||
            item.userName ||
            item.user ||
            item.clientName ||
            item.hubName ||
            item.categoryName ||
            item.name ||
            item._id ||
            "Record";

          const revenue =
            item.totalRevenue ??
            item.totalAmount ??
            item.amount ??
            item.revenue;

          const trips = item.totalTrips ?? item.tripCount ?? item.trips;

          if (revenue !== undefined && trips !== undefined) {
            return `${i + 1}. ${name} → ₹${Number(revenue).toLocaleString(
              "en-IN",
              { maximumFractionDigits: 2 },
            )} | Trips: ${trips}`;
          }

          const value =
            item.tripCount ??
            item.totalAmount ??
            item.totalExpense ??
            item.totalRevenue ??
            item.totalTrips ??
            item.totalKm ??
            item.totalkm ??
            item.dailyKm ??
            item.status ??
            item.count ??
            item.total ??
            "";

          return `${i + 1}. ${name}${value !== "" ? ` - ${value}` : ""}`;
        })
        .join("\n");

      return `Showing ${limit} of ${total} results\n\n${list}`;
    }

    if (data.message) return data.message;
    if (typeof data === "string") return data;

    return JSON.stringify(data, null, 2);
  };

  const showMoreData = (index) => {
    setChatMessages((prev) =>
      prev.map((msg, i) => {
        if (i !== index || !Array.isArray(msg.rawData?.data)) return msg;

        const currentLimit = msg.visibleLimit || INITIAL_VISIBLE_RESULTS;
        const newLimit = currentLimit + RESULTS_STEP;
        const total = msg.rawData.data.length;

        return {
          ...msg,
          visibleLimit: newLimit,
          text: formatResponse(msg.rawData, newLimit),
          showMore: newLimit < total,
        };
      }),
    );
  };

  const sendMessage = async (text) => {
    const message = (text || chatInput).trim();
    if (!message || loading) return;

    setChatMessages((prev) => [
      ...prev,
      { sender: "user", text: message, time: getTime() },
    ]);
    setChatInput("");
    setLoading(true);
    setShowSuggestions(false);

    try {
      const res = await axios.post(
        `${API_URL}/chat`,
        { question: message },
        { timeout: 60000 },
      );

      const totalResults = Array.isArray(res.data?.data)
        ? res.data.data.length
        : 0;

      setChatMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: formatResponse(res.data, INITIAL_VISIBLE_RESULTS),
          rawData: res.data,
          visibleLimit: INITIAL_VISIBLE_RESULTS,
          showMore: totalResults > INITIAL_VISIBLE_RESULTS,
          time: getTime(),
        },
      ]);
    } catch (err) {
      setChatMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text:
            err.response?.data?.detail ||
            "Sorry, I could not connect to the backend. Please try again.",
          isError: true,
          time: getTime(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setChatMessages([]);
    setShowSuggestions(false);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const scrollSuggestions = (direction) => {
    if (!suggestionsRef.current) return;

    const scrollAmount = 250;

    suggestionsRef.current.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <>
      <style>{`
        :root {
          --pickkup-primary: #f59e0b;
          --pickkup-primary-dark: #d97706;
          --pickkup-primary-soft: #fff7ed;
          --pickkup-text: #172033;
          --pickkup-muted: #6b7280;
          --pickkup-line: #e5e7eb;
          --pickkup-surface: rgba(255, 255, 255, 0.96);
          --pickkup-page: #f6f7fb;
        }

        .pk-chat-launcher {
          position: fixed;
          right: 24px;
          bottom: 24px;
          width: 62px;
          height: 62px;
          border: 0;
          border-radius: 20px;
          display: grid;
          place-items: center;
          color: white;
          background: linear-gradient(145deg, #fbbf24, #f59e0b 55%, #d97706);
          box-shadow:
            0 18px 35px rgba(217, 119, 6, 0.28),
            inset 0 1px 0 rgba(255, 255, 255, 0.35);
          cursor: pointer;
          z-index: 99999;
          transition: transform 180ms ease, box-shadow 180ms ease;
        }

        .pk-chat-launcher:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 22px 42px rgba(217, 119, 6, 0.34);
        }

        .pk-chat-launcher:active {
          transform: scale(0.96);
        }

        .pk-chat-panel {
          position: fixed;
          isolation: isolate;
          right: 24px;
          bottom: 98px;
          width: min(440px, calc(100vw - 32px));
          height: min(720px, calc(100vh - 125px));
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.75);
          border-radius: 28px;
          background: var(--pickkup-surface);
          box-shadow:
            0 30px 80px rgba(23, 32, 51, 0.18),
            0 8px 24px rgba(23, 32, 51, 0.08);
          backdrop-filter: blur(22px);
          -webkit-backdrop-filter: blur(22px);
          z-index: 99999;
          animation: pk-panel-in 200ms ease-out;
          font-family:
            Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
            "Segoe UI", sans-serif;
        }

        @keyframes pk-panel-in {
          from {
            opacity: 0;
            transform: translateY(14px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .pk-chat-header {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 18px 16px;
          color: white;
          background:
            radial-gradient(circle at 15% 0%, rgba(255,255,255,0.23), transparent 35%),
            linear-gradient(135deg, #fbbf24 0%, #f59e0b 48%, #d97706 100%);
        }

        .pk-chat-header::after {
          content: "";
          position: absolute;
          inset: auto 0 0;
          height: 1px;
          background: rgba(255,255,255,0.28);
        }

        .pk-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .pk-brand-icon {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          flex: 0 0 auto;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.2);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.28);
        }

        .pk-brand-copy {
          display: flex;
          flex-direction: column;

          align-items: flex-start;
          justify-content: center;

          min-width: 0;

          gap: 1px;
        }

        .pk-brand-title {
          margin: 0;
          padding: 0;

          color: #111827;

          font-size: 15px;
          font-weight: 750;

          line-height: 1.2;

          white-space: nowrap;
        }

        .pk-brand-status {
          display: flex;
          align-items: center;

          gap: 6px;

          margin: 0;
          padding: 0;

          color: rgba(255, 255, 255, 0.96);

          font-size: 10.5px;
          font-weight: 500;
          line-height: 1.15;

          white-space: nowrap;
        }

        .pk-status-dot {
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: #dcfce7;
          box-shadow: 0 0 0 3px rgba(220,252,231,0.18);
        }

        .pk-header-actions {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .pk-header-left {
          flex: 1;
          min-width: 0;

          display: flex;
          flex-direction: row;       /* IMPORTANT */
          align-items: center;       /* IMPORTANT */
          justify-content: flex-start;

          gap: 10px;

          flex-wrap: nowrap;         /* DON'T ALLOW TITLE TO DROP */
        }

        .pk-icon-button {
          width: 36px;
          height: 36px;
          display: grid;
          place-items: center;
          border: 0;
          border-radius: 12px;
          color: inherit;
          background: rgba(255,255,255,0.12);
          cursor: pointer;
          transition: background 160ms ease, transform 160ms ease;
        }

        .pk-icon-button:hover {
          background: rgba(255,255,255,0.22);
        }

        .pk-icon-button:active {
          transform: scale(0.94);
        }

        /* =========================
   MENU ICON
========================= */

.pk-menu-icon {
  width: 18px;
  height: 18px;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  gap: 3px;
}

.pk-menu-icon span {
  width: 15px;
  height: 1.6px;

  border-radius: 100px;

  background: currentColor;
}

.pk-icon-button.active {
  background: rgba(255, 255, 255, 0.28);
}

/* =========================================
   DRAWER BACKDROP
========================================= */

.pk-drawer-backdrop {
  position: absolute;

  top: 77px;

  left: 0;
  right: 0;
  bottom: 0;

  z-index: 40;

  background: rgba(17, 24, 39, 0.28);

  backdrop-filter: blur(1.5px);

  -webkit-backdrop-filter: blur(1.5px);

  animation: pk-backdrop-in 160ms ease;
}


@keyframes pk-backdrop-in {

  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}


/* =========================================
   DRAWER
========================================= */

.pk-suggestions-drawer {
  position: absolute;

  top: 77px;
  left: 0;
  bottom: 0;

  width: 78%;

  max-width: 320px;

  z-index: 50;

  display: flex;

  flex-direction: column;

  overflow: hidden;

  background: #ffffff;

  border-right: 1px solid #eceff3;

  box-shadow:
    16px 0 42px rgba(15, 23, 42, 0.14);

  animation:
    pk-drawer-open
    220ms
    cubic-bezier(0.22, 1, 0.36, 1);
}


@keyframes pk-drawer-open {

  from {
    opacity: 0;

    transform: translateX(-100%);
  }

  to {
    opacity: 1;

    transform: translateX(0);
  }
}


/* =========================================
   DRAWER HEADER
========================================= */

.pk-drawer-header {
  flex: 0 0 auto;

  display: flex;

  align-items: flex-start;

  justify-content: space-between;

  gap: 12px;

  padding: 16px 14px 13px;

  border-bottom: 1px solid #f0f1f3;

  background: #ffffff;
}


.pk-drawer-title {
  display: flex;

  align-items: center;

  gap: 8px;

  color: #c86f00;

  font-size: 13px;

  font-weight: 750;
}


.pk-drawer-subtitle {
  margin-top: 4px;

  margin-left: 25px;

  color: #9ca3af;

  font-size: 10px;

  font-weight: 500;

  line-height: 1.35;
}


/* close */

.pk-drawer-close {
  width: 30px;

  height: 30px;

  flex-shrink: 0;

  display: grid;

  place-items: center;

  border: 0;

  border-radius: 9px;

  background: #f6f7f9;

  color: #6b7280;

  cursor: pointer;

  transition:
    background 150ms ease,
    color 150ms ease,
    transform 150ms ease;
}


.pk-drawer-close:hover {
  background: #fff3df;

  color: #d97706;
}


.pk-drawer-close:active {
  transform: scale(0.94);
}


/* =========================================
   QUESTIONS LIST
========================================= */

.pk-drawer-list {
  flex: 1;

  min-height: 0;

  overflow-y: auto;

  padding: 9px 9px 14px;

  background: #ffffff;

  scrollbar-width: thin;

  scrollbar-color: #d1d5db transparent;
}


.pk-drawer-list::-webkit-scrollbar {
  width: 5px;
}


.pk-drawer-list::-webkit-scrollbar-track {
  background: transparent;
}


.pk-drawer-list::-webkit-scrollbar-thumb {
  background: #d1d5db;

  border-radius: 999px;
}


/* =========================================
   QUESTION
========================================= */

.pk-drawer-question {
  width: 100%;

  min-height: 43px;

  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 10px;

  margin-bottom: 5px;

  padding: 9px 10px;

  border: 1px solid transparent;

  border-radius: 10px;

  background: transparent;

  color: #374151;

  cursor: pointer;

  font: inherit;

  font-size: 11px;

  font-weight: 620;

  line-height: 1.3;

  text-align: left;

  transition:
    background 150ms ease,
    border-color 150ms ease,
    color 150ms ease,
    transform 150ms ease;
}


.pk-drawer-question:hover {
  background: #fff8ed;

  border-color: #f5dfbd;

  color: #9a5904;

  transform: translateX(2px);
}


.pk-drawer-question:active {
  transform: scale(0.985);
}


.pk-drawer-question:disabled {
  opacity: 0.5;

  cursor: not-allowed;

  transform: none;
}


.pk-question-text {
  flex: 1;

  min-width: 0;
}


.pk-question-arrow {
  flex-shrink: 0;

  color: #e58a0a;

  font-size: 18px;

  line-height: 1;

  opacity: 0.72;

  transition:
    opacity 150ms ease,
    transform 150ms ease;
}


.pk-drawer-question:hover .pk-question-arrow {
  opacity: 1;

  transform: translateX(2px);
}
  .pk-messages {
          flex: 1;
          overflow-y: auto;
          padding: 18px 14px 12px;
          background:
            radial-gradient(circle at 90% 5%, rgba(245,158,11,0.065), transparent 28%),
            var(--pickkup-page);
          scroll-behavior: smooth;
        }

        .pk-message-row {
          display: flex;
          gap: 9px;
          align-items: flex-end;
          margin-bottom: 16px;
        }

        .pk-message-row.user {
          justify-content: flex-end;
        }

        .pk-avatar {
          width: 30px;
          height: 30px;
          display: grid;
          place-items: center;
          flex: 0 0 auto;
          border: 1px solid #fde7bf;
          border-radius: 11px;
          color: var(--pickkup-primary-dark);
          background: white;
          box-shadow: 0 3px 10px rgba(23,32,51,0.06);
        }

        .pk-message-stack {
          display: flex;
          flex-direction: column;
          max-width: calc(100% - 44px);
        }

        .pk-message-row.user .pk-message-stack {
          align-items: flex-end;
        }

        .pk-message-bubble {
          max-width: 100%;
          text-align: left;
          padding: 12px 14px;
          border-radius: 18px;
          color: var(--pickkup-text);
          background: white;
          box-shadow: 0 5px 18px rgba(23,32,51,0.065);
          white-space: pre-wrap;
          overflow-wrap: anywhere;
          font-size: 13px;
          line-height: 1.6;
        }

        .pk-message-row.bot .pk-message-bubble {
          width: fit-content;
          border-bottom-left-radius: 6px;
        }

        .pk-message-row.user .pk-message-bubble {
          max-width: 84%;
          color: white;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          border-bottom-right-radius: 6px;
          box-shadow: 0 8px 20px rgba(217,119,6,0.18);
        }

        .pk-message-bubble.error {
          color: #991b1b;
          border: 1px solid #fecaca;
          background: #fff1f2;
        }

        .pk-message-time {
          margin-top: 5px;
          padding: 0 4px;
          color: #9ca3af;
          font-size: 10px;
        }

        .pk-show-more {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          margin-top: 10px;
          padding: 7px 10px;
          border: 1px solid #fed7aa;
          border-radius: 10px;
          color: var(--pickkup-primary-dark);
          background: #fff7ed;
          cursor: pointer;
          font: inherit;
          font-size: 11px;
          font-weight: 750;
        }

        .pk-typing {
          display: flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 14px;
        }

        .pk-typing-bubble {
          display: flex;
          gap: 4px;
          padding: 13px 15px;
          border-radius: 17px 17px 17px 6px;
          background: white;
          box-shadow: 0 5px 18px rgba(23,32,51,0.065);
        }

        .pk-typing-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #f59e0b;
          animation: pk-bounce 1s infinite ease-in-out;
        }

        .pk-launcher-logo {
  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 25px;
  line-height: 1;

  width: 42px;
  height: 42px;

  transform: scale(1.15);
}

        .pk-typing-dot:nth-child(2) { animation-delay: 0.15s; }
        .pk-typing-dot:nth-child(3) { animation-delay: 0.3s; }

        @keyframes pk-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.45; }
          30% { transform: translateY(-4px); opacity: 1; }
        }

        .pk-composer-wrap {
          padding: 12px;
          border-top: 1px solid var(--pickkup-line);
          background: rgba(255,255,255,0.96);
        }

        .pk-composer {
          display: flex;
          align-items: flex-end;
          gap: 9px;
          padding: 7px 7px 7px 13px;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          background: #f9fafb;
          transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
        }

        .pk-composer:focus-within {
          border-color: #fbbf24;
          background: white;
          box-shadow: 0 0 0 4px rgba(245,158,11,0.10);
        }

        .pk-input {
          width: 100%;
          min-height: 24px;
          max-height: 96px;
          resize: none;
          overflow-y: auto;
          padding: 5px 0;
          border: 0;
          outline: none;
          color: var(--pickkup-text);
          background: transparent;
          font: inherit;
          font-size: 13px;
          line-height: 1.45;
        }

        .pk-input::placeholder {
          color: #9ca3af;
        }

        .pk-send {
          width: 40px;
          height: 40px;
          display: grid;
          place-items: center;
          flex: 0 0 auto;
          border: 0;
          border-radius: 13px;
          color: white;
          background: linear-gradient(135deg, #fbbf24, #f59e0b 50%, #d97706);
          box-shadow: 0 7px 16px rgba(217,119,6,0.2);
          cursor: pointer;
          transition: transform 160ms ease, opacity 160ms ease;
        }

        .pk-send:hover:not(:disabled) {
          transform: translateY(-1px);
        }

        .pk-send:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          box-shadow: none;
        }

        .pk-composer-note {
          margin: 7px 2px 0;
          color: #a1a1aa;
          text-align: center;
          font-size: 9px;
        }

        @media (max-width: 600px) {
          .pk-chat-launcher {
            right: 16px;
            bottom: 16px;
          }

          .pk-chat-panel {
            inset: 0;
            width: 100vw;
            height: 100dvh;
            max-height: none;
            border: 0;
            border-radius: 0;
          }

          .pk-chat-header {
            padding-top: max(16px, env(safe-area-inset-top));
          }

          .pk-composer-wrap {
            padding-bottom: max(12px, env(safe-area-inset-bottom));
          }
          .pk-drawer-backdrop,
          .pk-suggestions-drawer {
            top: 76px;
          }

          .pk-suggestions-drawer {
            width: min(82%, 320px);
          }

          .pk-drawer-header {
            padding: 14px 12px 12px;
          }

          .pk-drawer-question {
            min-height: 42px;

            padding: 9px 10px;

            font-size: 10.5px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .pk-chat-panel,
          .pk-chat-launcher,
          .pk-chevron,
          .pk-suggestion-chip,
          .pk-send {
            animation: none;
            transition: none;
          }
        }
      `}</style>

      <button
        className="pk-chat-launcher"
        onClick={() => setChatOpen((open) => !open)}
        aria-label={chatOpen ? "Close chat" : "Open chat"}
        title={chatOpen ? "Close chat" : "Open chat"}
      >
        <span className="pk-launcher-logo">💬</span>
      </button>

      {chatOpen && (
        <section className="pk-chat-panel" aria-label={`${title} chat`}>
          <header className="pk-chat-header">
            {/* LEFT SIDE */}
            <div className="pk-header-left">
              {/* Menu button */}
              <button
                className={`pk-icon-button pk-menu-button ${
                  showSuggestions ? "active" : ""
                }`}
                onClick={() => setShowSuggestions((prev) => !prev)}
                aria-label="Suggested questions"
                title="Suggested questions"
              >
                <span className="pk-menu-icon">
                  <span />
                  <span />
                  <span />
                </span>
              </button>

              {/* Title + Online status */}
              <div className="pk-brand-copy">
                <h2 className="pk-brand-title">{title}</h2>

                <div className="pk-brand-status">
                  <span className="pk-status-dot" />
                  Online and ready to help
                </div>
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="pk-header-actions">
              <button
                className="pk-icon-button"
                onClick={clearChat}
                aria-label="Clear chat"
                title="Clear chat"
              >
                <Icon name="trash" size={17} />
              </button>

              <button
                className="pk-icon-button"
                onClick={() => setChatOpen(false)}
                aria-label="Close chat"
                title="Close chat"
              >
                <Icon name="close" size={19} />
              </button>
            </div>
          </header>
          {/* =========================
    SUGGESTIONS DRAWER
========================= */}

          {showSuggestions && (
            <>
              {/* Dark transparent background */}
              <div
                className="pk-drawer-backdrop"
                onClick={() => setShowSuggestions(false)}
              />

              {/* Drawer */}
              <aside className="pk-suggestions-drawer">
                <div className="pk-drawer-header">
                  <div>
                    <div className="pk-drawer-title">
                      <Icon name="sparkle" size={17} />
                      Suggested questions
                    </div>

                    <div className="pk-drawer-subtitle">
                      Quick questions about your fleet
                    </div>
                  </div>

                  <button
                    className="pk-drawer-close"
                    onClick={() => setShowSuggestions(false)}
                    aria-label="Close suggestions"
                  >
                    <Icon name="close" size={18} />
                  </button>
                </div>

                <div className="pk-drawer-list">
                  {defaultSuggestions.map((item, index) => (
                    <button
                      key={`${item.label}-${index}`}
                      className="pk-drawer-question"
                      disabled={loading}
                      onClick={() => {
                        setShowSuggestions(false);
                        sendMessage(item.question);
                      }}
                    >
                      <span className="pk-question-text">{item.label}</span>

                      <span className="pk-question-arrow">›</span>
                    </button>
                  ))}
                </div>
              </aside>
            </>
          )}
          <main className="pk-messages">
            {chatMessages.map((msg, index) => (
              <div
                key={`${msg.time}-${index}`}
                className={`pk-message-row ${msg.sender}`}
              >
                {msg.sender === "bot" && (
                  <div className="pk-avatar">
                    <Icon name="bot" size={16} />
                  </div>
                )}

                <div className="pk-message-stack">
                  <div
                    className={`pk-message-bubble ${
                      msg.isError ? "error" : ""
                    }`}
                  >
                    {msg.text}

                    {msg.sender === "bot" && msg.showMore && (
                      <button
                        className="pk-show-more"
                        onClick={() => showMoreData(index)}
                      >
                        <Icon name="plus" size={14} />
                        Show more results
                      </button>
                    )}
                  </div>

                  <span className="pk-message-time">{msg.time}</span>
                </div>
              </div>
            ))}

            {loading && (
              <div className="pk-typing" aria-label="Assistant is typing">
                <div className="pk-avatar">
                  <Icon name="bot" size={16} />
                </div>
                <div className="pk-typing-bubble">
                  <span className="pk-typing-dot" />
                  <span className="pk-typing-dot" />
                  <span className="pk-typing-dot" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </main>

          <footer className="pk-composer-wrap">
            <div className="pk-composer">
              <textarea
                ref={inputRef}
                className="pk-input"
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder="Ask anything about your fleet..."
                rows={1}
                disabled={loading}
                aria-label="Chat message"
              />

              <button
                className="pk-send"
                disabled={loading || !chatInput.trim()}
                onClick={() => sendMessage()}
                aria-label="Send message"
                title="Send"
              >
                <Icon name="send" size={18} />
              </button>
            </div>
          </footer>
        </section>
      )}
    </>
  );
}
